import { useState, useContext } from "react";
import authFetch from "../authFetch";

/* ── Notification events catalogue ── */
/* ── Per-event configurable parameters ─────────────────── */
const EVENT_PARAMS={
  large_expense_alert:[{key:"threshold",label:"Amount threshold (SAR)",type:"number",default:5000}],
  cashflow_alert:[{key:"thresholdPercent",label:"Deficit threshold (%)",type:"number",default:20}],
};

/* ── Template variables available per event ─────────────── */
const EVENT_VARIABLES={
  registration_email_verify:["code","email","platformName"],
  registration_password_reset:["code","email","platformName"],
  registration_phone_verify:["code","phone","platformName"],
  registration_welcome:["name","email","workspaceName","platformName"],
  registration_pending:["name","email","workspaceName","platformName"],
  billing_invoice:["ownerName","workspaceName","planName","amount","currency","billingCycle","invoiceNumber","periodStart","periodEnd","platformName"],
  billing_payment_failed:["ownerName","workspaceName","planName","amount","currency","platformName"],
  billing_cancelled:["ownerName","workspaceName","endsAt","platformName"],
  billing_expiring:["ownerName","workspaceName","daysRemaining","platformName"],
  expense_submitted:["amount","description","submitter","category","date"],
  expense_approved:["amount","description"],
  expense_rejected:["amount","description","reason"],
  large_expense_alert:["amount","description","submitter","threshold"],
  recurring_expense_due:["name","amount","dueDate"],
  income_received:["amount","source","category"],
  income_overdue:["amount","source","dueDate"],
  recurring_income_due:["name","amount","dueDate"],
  weekly_finance_summary:["totalIncome","totalExpenses","net"],
  monthly_finance_report:["totalIncome","totalExpenses","net","topCategory"],
  cashflow_alert:["totalExpenses","totalIncome","net"],
  salary_processed:["staffName","amount","period"],
  loan_disbursed:["staffName","amount","description"],
  investment_update:["totalValue","changePercent"],
  interest_payment_due:["liabilityName","amount","dueDate","lender"],
  liability_maturity_alert:["liabilityName","maturityDate","outstandingBalance","lender"],
  investment_pnl_alert:["holdingName","changePercent","currentValue"],
  leave_requested:["staffName","startDate","endDate"],
  leave_approved:["staffName"],
  transport_request:["pickup","dropoff","date","time"],
  transport_completed:["pickup","dropoff","date","time","driverName"],
  transport_cancelled:["pickup","dropoff","date","time","reason"],
  transport_conflict:["pickup","dropoff","date","time","driverName","conflictTime"],
  maintenance_due:["vehicle","serviceType"],
  low_stock:["item","currentQty"],
  meeting_reminder:["title","time","location"],
};
const COMMON_VARIABLES=["appName","recipientName","date","time"];

/* ── Sample data for template previews ─────────────────── */
const EVENT_SAMPLE_DATA={
  amount:"SAR 5,000",description:"Office supplies",submitter:"Khalid",category:"Business",
  date:"2026-03-26",reason:"Exceeds budget",name:"Monthly Rent",dueDate:"2026-04-01",
  source:"Tower B Rental",totalIncome:"SAR 85,000",totalExpenses:"SAR 62,000",net:"SAR 23,000",
  topCategory:"Household",staffName:"Ahmed",period:"March 2026",totalValue:"SAR 1,200,000",
  changePercent:"+3.2%",startDate:"2026-04-01",endDate:"2026-04-05",
  pickup:"Home",dropoff:"Office",time:"09:00",vehicle:"2024 Toyota Land Cruiser",
  serviceType:"Oil Change",item:"Rice (5kg)",currentQty:"2",title:"Board Meeting",
  location:"Conference Room A",threshold:"SAR 5,000",appName:"Command",recipientName:"Talal",
  ownerName:"Talal",workspaceName:"Al Rashid Family Office",planName:"Pro",
  invoiceNumber:"INV-2026-0001",periodStart:"2026-04-07",periodEnd:"2026-05-07",
  billingCycle:"monthly",endsAt:"2026-05-07",daysRemaining:"3",
};

/* ── Default email HTML per event ──────────────────────── */
function defaultEmailHtml(event,vars){
  const v={...EVENT_SAMPLE_DATA,...vars};
  const s=EVENT_VARIABLES[event]||[];
  const body=s.map(k=>`<tr><td style="padding:6px 12px;font-size:13px;color:#666;border-bottom:1px solid #f0f0f0;">${k}</td><td style="padding:6px 12px;font-size:13px;font-weight:600;color:#1A1714;border-bottom:1px solid #f0f0f0;">{{${k}}}</td></tr>`).join("");
  return `<div style="max-width:500px;margin:0 auto;font-family:sans-serif;"><div style="background:#C9A84C;padding:16px 24px;border-radius:10px 10px 0 0;"><div style="font-size:15px;font-weight:700;color:white;">{{appName}}</div></div><div style="background:white;padding:24px;border:1px solid #eee;border-top:none;border-radius:0 0 10px 10px;"><h2 style="margin:0 0 16px;font-size:16px;color:#1A1714;">${(NOTIF_EVENTS_MAP||{})[event]||event}</h2><table style="width:100%;border-collapse:collapse;">${body}</table><p style="margin:16px 0 0;font-size:11px;color:#aaa;">Sent by {{appName}} — {{date}} at {{time}}</p></div></div>`;
}

/* Helper: substitute {{var}} placeholders */
function applyTemplate(tpl,vars){if(!tpl)return"";return tpl.replace(/\{\{(\w+)\}\}/g,(_,k)=>vars[k]??"");}

/* ── Bilingual notification text ─────────────────────────
   Each event has { en: { title, body(data) }, ar: { title, body(data) } }
   body is a function receiving eventData + helpers
   ──────────────────────────────────────────────────────── */
const _fmtN=(n)=>(n||0).toLocaleString(undefined,{maximumFractionDigits:0});
const NOTIF_TEXT={
  /* Registration */
  registration_email_verify:{
    en:{title:"Email Verification",body:d=>`Your verification code is: ${d.code||""}`},
    ar:{title:"التحقق من البريد الإلكتروني",body:d=>`رمز التحقق الخاص بك هو: ${d.code||""}`},
  },
  registration_password_reset:{
    en:{title:"Password Reset",body:d=>`Your password reset code is: ${d.code||""}`},
    ar:{title:"إعادة تعيين كلمة المرور",body:d=>`رمز إعادة تعيين كلمة المرور: ${d.code||""}`},
  },
  registration_phone_verify:{
    en:{title:"Phone Verification",body:d=>`Your verification code is: ${d.code||""}`},
    ar:{title:"التحقق من رقم الهاتف",body:d=>`رمز التحقق الخاص بك هو: ${d.code||""}`},
  },
  registration_welcome:{
    en:{title:"Welcome!",body:d=>`Welcome to ${d.appName||"Command"}, ${d.name||""}! Your workspace is ready.`},
    ar:{title:"!أهلاً وسهلاً",body:d=>`مرحباً بك في ${d.appName||"Command"}، ${d.name||""}! مساحة العمل جاهزة.`},
  },
  registration_pending:{
    en:{title:"Registration Pending",body:d=>`${d.name||"A user"} (${d.email||""}) has registered and is awaiting approval.`},
    ar:{title:"تسجيل قيد الانتظار",body:d=>`${d.name||"مستخدم"} (${d.email||""}) قام بالتسجيل وبانتظار الموافقة.`},
  },
  /* Billing */
  billing_invoice:{
    en:{title:"Payment Receipt",body:d=>`Invoice ${d.invoiceNumber||""} — ${d.currency||"SAR"} ${d.amount||"0"} for ${d.planName||"subscription"} (${d.billingCycle||"monthly"}).\nPeriod: ${d.periodStart||""} to ${d.periodEnd||""}.`},
    ar:{title:"إيصال الدفع",body:d=>`فاتورة ${d.invoiceNumber||""} — ${d.currency||"SAR"} ${d.amount||"0"} لـ ${d.planName||"الاشتراك"} (${d.billingCycle==="yearly"?"سنوي":"شهري"}).\nالفترة: ${d.periodStart||""} إلى ${d.periodEnd||""}.`},
  },
  billing_payment_failed:{
    en:{title:"Payment Failed",body:d=>`We couldn't process your payment of ${d.currency||"SAR"} ${d.amount||"0"} for the ${d.planName||""} plan. Please update your payment method to avoid service interruption.`},
    ar:{title:"فشل الدفع",body:d=>`لم نتمكن من معالجة دفعتك بمبلغ ${d.currency||"SAR"} ${d.amount||"0"} لخطة ${d.planName||""}. يرجى تحديث طريقة الدفع لتجنب انقطاع الخدمة.`},
  },
  billing_cancelled:{
    en:{title:"Subscription Cancelled",body:d=>`Your subscription for ${d.workspaceName||""} has been cancelled. You will retain access until ${d.endsAt||"the end of your billing period"}.`},
    ar:{title:"تم إلغاء الاشتراك",body:d=>`تم إلغاء اشتراكك في ${d.workspaceName||""}. ستحتفظ بالوصول حتى ${d.endsAt||"نهاية فترة الفوترة"}.`},
  },
  billing_expiring:{
    en:{title:"Subscription Expiring Soon",body:d=>`Your subscription for ${d.workspaceName||""} expires in ${d.daysRemaining||"a few"} days. Renew now to avoid losing access.`},
    ar:{title:"الاشتراك ينتهي قريباً",body:d=>`اشتراكك في ${d.workspaceName||""} ينتهي خلال ${d.daysRemaining||"بضعة"} أيام. جدد الآن لتجنب فقدان الوصول.`},
  },
  /* Finance — Expenses */
  expense_submitted:{
    en:{title:"New Expense Submitted",body:d=>`SAR ${_fmtN(d.amount)} — ${d.notes||d.description||"No description"}\nBy: ${d.submitterName||d.submitter||"Unknown"}`},
    ar:{title:"مصروف جديد مقدّم",body:d=>`ر.س ${_fmtN(d.amount)} — ${d.notes||d.description||"بدون وصف"}\nمن: ${d.submitterName||d.submitter||"غير معروف"}`},
  },
  expense_approved:{
    en:{title:"Expense Approved",body:d=>`SAR ${_fmtN(d.amount)} expense has been approved.${d.notes?"\n"+d.notes:""}`},
    ar:{title:"تمت الموافقة على المصروف",body:d=>`تمت الموافقة على مصروف بقيمة ر.س ${_fmtN(d.amount)}.${d.notes?"\n"+d.notes:""}`},
  },
  expense_rejected:{
    en:{title:"Expense Rejected",body:d=>`SAR ${_fmtN(d.amount)} — ${d.notes||d.description||"No description"}\nReason: ${d.reason||"Not specified"}`},
    ar:{title:"تم رفض المصروف",body:d=>`ر.س ${_fmtN(d.amount)} — ${d.notes||d.description||"بدون وصف"}\nالسبب: ${d.reason||"غير محدد"}`},
  },
  large_expense_alert:{
    en:{title:"Large Expense Alert",body:d=>`SAR ${_fmtN(d.amount)} — ${d.notes||d.description||"No description"}\nBy: ${d.submitterName||d.submitter||"Unknown"}\nThis expense exceeds the alert threshold.`},
    ar:{title:"تنبيه مصروف كبير",body:d=>`ر.س ${_fmtN(d.amount)} — ${d.notes||d.description||"بدون وصف"}\nمن: ${d.submitterName||d.submitter||"غير معروف"}\nهذا المصروف يتجاوز حد التنبيه.`},
  },
  recurring_expense_due:{
    en:{title:"Recurring Expense Due",body:d=>`${d.name||"Recurring expense"} — SAR ${_fmtN(d.amount)} due on ${d.dueDate||"today"}`},
    ar:{title:"مصروف دوري مستحق",body:d=>`${d.name||"مصروف دوري"} — ر.س ${_fmtN(d.amount)} مستحق في ${d.dueDate||"اليوم"}`},
  },
  /* Finance — Income */
  income_received:{
    en:{title:"Income Received",body:d=>`SAR ${_fmtN(d.amount)} — ${d.source||"Unknown source"}\nCategory: ${d.catName||d.category||""}`},
    ar:{title:"دخل مُستلم",body:d=>`ر.س ${_fmtN(d.amount)} — ${d.source||"مصدر غير معروف"}\nالفئة: ${d.catName||d.category||""}`},
  },
  income_overdue:{
    en:{title:"Income Overdue",body:d=>`SAR ${_fmtN(d.amount)} — ${d.source||"Unknown source"}\nDue: ${d.dueDate||"Unknown"}\nThis unrealised income is past its due date.`},
    ar:{title:"دخل متأخر",body:d=>`ر.س ${_fmtN(d.amount)} — ${d.source||"مصدر غير معروف"}\nالاستحقاق: ${d.dueDate||"غير معروف"}\nهذا الدخل غير المحقق تجاوز تاريخ استحقاقه.`},
  },
  recurring_income_due:{
    en:{title:"Recurring Income Expected",body:d=>`${d.name||"Recurring income"} — SAR ${_fmtN(d.amount)} expected on ${d.dueDate||"today"}`},
    ar:{title:"دخل دوري متوقع",body:d=>`${d.name||"دخل دوري"} — ر.س ${_fmtN(d.amount)} متوقع في ${d.dueDate||"اليوم"}`},
  },
  /* Finance — Reports */
  weekly_finance_summary:{
    en:{title:"Weekly Finance Summary",body:d=>`Income: SAR ${_fmtN(d.totalIncome)}\nExpenses: SAR ${_fmtN(d.totalExpenses)}\nNet: SAR ${_fmtN((d.totalIncome||0)-(d.totalExpenses||0))}`},
    ar:{title:"ملخص مالي أسبوعي",body:d=>`الدخل: ر.س ${_fmtN(d.totalIncome)}\nالمصروفات: ر.س ${_fmtN(d.totalExpenses)}\nالصافي: ر.س ${_fmtN((d.totalIncome||0)-(d.totalExpenses||0))}`},
  },
  monthly_finance_report:{
    en:{title:"Monthly Finance Report",body:d=>`Income: SAR ${_fmtN(d.totalIncome)}\nExpenses: SAR ${_fmtN(d.totalExpenses)}\nNet: SAR ${_fmtN((d.totalIncome||0)-(d.totalExpenses||0))}${d.topCategory?"\nTop expense: "+d.topCategory:""}`},
    ar:{title:"تقرير مالي شهري",body:d=>`الدخل: ر.س ${_fmtN(d.totalIncome)}\nالمصروفات: ر.س ${_fmtN(d.totalExpenses)}\nالصافي: ر.س ${_fmtN((d.totalIncome||0)-(d.totalExpenses||0))}${d.topCategory?"\nأعلى فئة: "+d.topCategory:""}`},
  },
  cashflow_alert:{
    en:{title:"Cash Flow Warning",body:d=>`Expenses (SAR ${_fmtN(d.totalExpenses)}) exceed Income (SAR ${_fmtN(d.totalIncome)}) this month.\nNet: SAR ${_fmtN((d.totalIncome||0)-(d.totalExpenses||0))}`},
    ar:{title:"تحذير التدفق النقدي",body:d=>`المصروفات (ر.س ${_fmtN(d.totalExpenses)}) تتجاوز الدخل (ر.س ${_fmtN(d.totalIncome)}) هذا الشهر.\nالصافي: ر.س ${_fmtN((d.totalIncome||0)-(d.totalExpenses||0))}`},
  },
  /* Investments */
  investment_update:{
    en:{title:"Portfolio Update",body:d=>`Total value: SAR ${_fmtN(d.totalValue)}${d.changePercent!=null?"\nChange: "+(d.changePercent>=0?"+":"")+parseFloat(d.changePercent).toFixed(1)+"%":""}`},
    ar:{title:"تحديث المحفظة",body:d=>`القيمة الإجمالية: ر.س ${_fmtN(d.totalValue)}${d.changePercent!=null?"\nالتغيير: "+(d.changePercent>=0?"+":"")+parseFloat(d.changePercent).toFixed(1)+"%":""}`},
  },
  interest_payment_due:{
    en:{title:"Interest Payment Due",body:d=>`${d.liabilityName||d.name||"Liability"} — ${d.currency||"SAR"} ${_fmtN(d.periodAmount||d.amount)} due ${d.dueLabel||d.dueDate||"soon"}${d.dueDate?" ("+d.dueDate+")":""}${d.lender?"\nLender: "+d.lender:""}`},
    ar:{title:"دفعة فائدة مستحقة",body:d=>`${d.liabilityName||d.name||"التزام"} — ${d.currency||"ر.س"} ${_fmtN(d.periodAmount||d.amount)} مستحقة ${d.dueLabelAr||d.dueLabel||d.dueDate||"قريباً"}${d.dueDate?" ("+d.dueDate+")":""}${d.lender?"\nالمُقرض: "+d.lender:""}`},
  },
  liability_maturity_alert:{
    en:{title:"Liability Maturing Soon",body:d=>`${d.liabilityName||d.name||"Liability"} matures ${d.dueLabel||d.maturityDate||"soon"}${d.maturityDate?" ("+d.maturityDate+")":""}\nOutstanding: ${d.currency||"SAR"} ${_fmtN(d.outstandingBalance)}${d.lender?"\nLender: "+d.lender:""}`},
    ar:{title:"التزام يقترب من الاستحقاق",body:d=>`${d.liabilityName||d.name||"التزام"} يستحق ${d.dueLabelAr||d.dueLabel||d.maturityDate||"قريباً"}${d.maturityDate?" ("+d.maturityDate+")":""}\nالرصيد القائم: ${d.currency||"ر.س"} ${_fmtN(d.outstandingBalance)}${d.lender?"\nالمُقرض: "+d.lender:""}`},
  },
  investment_pnl_alert:{
    en:{title:"Significant P&L Change",body:d=>`${d.holdingName||"Holding"}: ${d.changePercent>=0?"+":""}${parseFloat(d.changePercent||0).toFixed(1)}%\nCurrent value: SAR ${_fmtN(d.currentValue)}`},
    ar:{title:"تغيير كبير في الربح/الخسارة",body:d=>`${d.holdingName||"استثمار"}: ${d.changePercent>=0?"+":""}${parseFloat(d.changePercent||0).toFixed(1)}%\nالقيمة الحالية: ر.س ${_fmtN(d.currentValue)}`},
  },
  /* Staff */
  salary_processed:{
    en:{title:"Salary Processed",body:d=>`${d.staffName||"Staff"} — SAR ${_fmtN(d.amount)} for ${d.period||"this month"}`},
    ar:{title:"تمت معالجة الراتب",body:d=>`${d.staffName||"موظف"} — ر.س ${_fmtN(d.amount)} عن ${d.period||"هذا الشهر"}`},
  },
  loan_disbursed:{
    en:{title:"Staff Loan Disbursed",body:d=>`SAR ${_fmtN(d.amount)} disbursed to ${d.staffName||"staff member"}${d.notes?"\n"+d.notes:""}`},
    ar:{title:"تم صرف سلفة موظف",body:d=>`ر.س ${_fmtN(d.amount)} تم صرفها إلى ${d.staffName||"موظف"}${d.notes?"\n"+d.notes:""}`},
  },
  leave_requested:{
    en:{title:"Leave Request",body:d=>`${d.staffName||"Staff"} requests leave from ${d.startDate||"?"} to ${d.endDate||"?"}`},
    ar:{title:"طلب إجازة",body:d=>`${d.staffName||"موظف"} يطلب إجازة من ${d.startDate||"?"} إلى ${d.endDate||"?"}`},
  },
  leave_approved:{
    en:{title:"Leave Approved",body:()=>"Leave request has been approved."},
    ar:{title:"تمت الموافقة على الإجازة",body:()=>"تمت الموافقة على طلب الإجازة."},
  },
  /* Operations */
  transport_request:{
    en:{title:"Transport Request",body:d=>`${d.pickupLoc||d.pickup||"Pickup"} → ${d.dropoffLoc||d.dropoff||"Dropoff"}\nDate: ${d.date||"TBD"} at ${d.time||"TBD"}`},
    ar:{title:"طلب نقل",body:d=>`${d.pickupLoc||d.pickup||"نقطة الانطلاق"} → ${d.dropoffLoc||d.dropoff||"الوجهة"}\nالتاريخ: ${d.date||"يحدد لاحقاً"} الساعة ${d.time||"يحدد لاحقاً"}`},
  },
  transport_completed:{
    en:{title:"Ride Completed",body:d=>`${d.pickup||"Pickup"} → ${d.dropoff||"Dropoff"} on ${d.date||"—"} at ${d.time||"—"}${d.driverName?" · Driver: "+d.driverName:""}`},
    ar:{title:"رحلة مكتملة",body:d=>`${d.pickup||"نقطة الانطلاق"} → ${d.dropoff||"الوجهة"} بتاريخ ${d.date||"—"} الساعة ${d.time||"—"}${d.driverName?" · السائق: "+d.driverName:""}`},
  },
  transport_cancelled:{
    en:{title:"Ride Cancelled",body:d=>`${d.pickup||"Pickup"} → ${d.dropoff||"Dropoff"} on ${d.date||"—"} has been cancelled.${d.reason?" Reason: "+d.reason:""}`},
    ar:{title:"رحلة ملغاة",body:d=>`${d.pickup||"نقطة الانطلاق"} → ${d.dropoff||"الوجهة"} بتاريخ ${d.date||"—"} تم إلغاؤها.${d.reason?" السبب: "+d.reason:""}`},
  },
  transport_conflict:{
    en:{title:"Driver Conflict",body:d=>`${d.driverName||"Driver"} has a scheduling conflict: ${d.pickup||"Pickup"} → ${d.dropoff||"Dropoff"} at ${d.time||"—"} on ${d.date||"—"}.${d.conflictTime?" Existing ride at "+d.conflictTime:""}`},
    ar:{title:"تعارض سائق",body:d=>`${d.driverName||"السائق"} لديه تعارض في الجدول: ${d.pickup||"نقطة الانطلاق"} → ${d.dropoff||"الوجهة"} الساعة ${d.time||"—"} بتاريخ ${d.date||"—"}.${d.conflictTime?" رحلة موجودة الساعة "+d.conflictTime:""}`},
  },
  /* Fleet */
  maintenance_due:{
    en:{title:"Maintenance Due",body:d=>`${d.vehicle||"Vehicle"}: ${d.serviceType||"Service"} is due.`},
    ar:{title:"صيانة مستحقة",body:d=>`${d.vehicle||"مركبة"}: ${d.serviceType||"خدمة"} مستحقة.`},
  },
  vehicle_added:{
    en:{title:"New Vehicle Added",body:d=>`${d.vehicle||"Vehicle"} has been added to the fleet.`},
    ar:{title:"تمت إضافة مركبة جديدة",body:d=>`${d.vehicle||"مركبة"} تمت إضافتها إلى الأسطول.`},
  },
  vehicle_updated:{
    en:{title:"Vehicle Updated",body:d=>`${d.vehicle||"Vehicle"} details have been updated.`},
    ar:{title:"تم تحديث المركبة",body:d=>`${d.vehicle||"مركبة"} تم تحديث بياناتها.`},
  },
  insurance_expiring:{
    en:{title:"Insurance Expiring",body:d=>`${d.vehicle||"Vehicle"}: insurance expires on ${d.expiryDate||"soon"}.`},
    ar:{title:"التأمين على وشك الانتهاء",body:d=>`${d.vehicle||"مركبة"}: التأمين ينتهي في ${d.expiryDate||"قريباً"}.`},
  },
  istimara_expiring:{
    en:{title:"Istimara Expiring",body:d=>`${d.vehicle||"Vehicle"}: registration expires on ${d.expiryDate||"soon"}.`},
    ar:{title:"الاستمارة على وشك الانتهاء",body:d=>`${d.vehicle||"مركبة"}: الاستمارة تنتهي في ${d.expiryDate||"قريباً"}.`},
  },
  /* Facility */
  /* Suppliers */
  supplier_added:{
    en:{title:"New Supplier Added",body:d=>`"${d.name||"Supplier"}" has been added${d.module?" ("+d.module+")":""}${d.category?" — "+d.category:""}.`},
    ar:{title:"تمت إضافة مورد جديد",body:d=>`"${d.name||"مورد"}" تمت إضافته${d.module?" ("+d.module+")":""}${d.category?" — "+d.category:""}.`},
  },
  supplier_updated:{
    en:{title:"Supplier Updated",body:d=>`"${d.name||"Supplier"}" details have been updated.`},
    ar:{title:"تم تحديث المورد",body:d=>`"${d.name||"مورد"}" تم تحديث بياناته.`},
  },
  supplier_deactivated:{
    en:{title:"Supplier Deactivated",body:d=>`"${d.name||"Supplier"}" has been set to ${d.status||"inactive"}.`},
    ar:{title:"تم تعطيل المورد",body:d=>`"${d.name||"مورد"}" تم تغيير حالته إلى ${d.status==="suspended"?"معلق":"غير نشط"}.`},
  },
  supplier_contract_expiring:{
    en:{title:"Supplier Contract Expiring",body:d=>`"${d.name||"Supplier"}" contract expires on ${d.contractExpiry||"soon"}.`},
    ar:{title:"عقد المورد على وشك الانتهاء",body:d=>`"${d.name||"مورد"}" عقده ينتهي في ${d.contractExpiry||"قريباً"}.`},
  },
  /* Projects */
  project_created:{
    en:{title:"Project Created",body:d=>`"${d.projectName||"A project"}" has been created.`},
    ar:{title:"تم إنشاء مشروع",body:d=>`"${d.projectName||"مشروع"}" تم إنشاؤه.`},
  },
  project_status_changed:{
    en:{title:"Project Status Changed",body:d=>`"${d.projectName||"Project"}" status changed to ${d.newStatus||"updated"}.`},
    ar:{title:"تغيير حالة المشروع",body:d=>`"${d.projectName||"مشروع"}" تم تغيير حالته إلى ${d.newStatus||"محدث"}.`},
  },
  project_deleted:{
    en:{title:"Project Deleted",body:d=>`"${d.projectName||"A project"}" has been deleted.`},
    ar:{title:"تم حذف المشروع",body:d=>`"${d.projectName||"مشروع"}" تم حذفه.`},
  },
  project_over_budget:{
    en:{title:"Project Over Budget",body:d=>`"${d.projectName||"Project"}" has exceeded its budget.`},
    ar:{title:"المشروع تجاوز الميزانية",body:d=>`"${d.projectName||"مشروع"}" تجاوز ميزانيته.`},
  },
  project_member_added:{
    en:{title:"Added to Project",body:d=>`${d.memberName||"A member"} was added to "${d.projectName||"a project"}".`},
    ar:{title:"تمت إضافة عضو للمشروع",body:d=>`تمت إضافة ${d.memberName||"عضو"} إلى "${d.projectName||"مشروع"}".`},
  },
  project_member_removed:{
    en:{title:"Removed from Project",body:d=>`${d.memberName||"A member"} was removed from "${d.projectName||"a project"}".`},
    ar:{title:"إزالة عضو من المشروع",body:d=>`تمت إزالة ${d.memberName||"عضو"} من "${d.projectName||"مشروع"}".`},
  },
  facility_issue_created:{
    en:{title:"Facility Issue Reported",body:d=>`${d.title||d.description||"An issue"} has been reported.${d.location?"\nLocation: "+d.location:""}`},
    ar:{title:"بلاغ مرفق جديد",body:d=>`تم الإبلاغ عن ${d.title||d.description||"مشكلة"}.${d.location?"\nالموقع: "+d.location:""}`},
  },
  facility_issue_status_changed:{
    en:{title:"Facility Issue Updated",body:d=>`"${d.title||"Issue"}" status changed to ${d.status||"updated"}.`},
    ar:{title:"تحديث بلاغ المرفق",body:d=>`"${d.title||"بلاغ"}" تم تغيير حالته إلى ${d.status||"محدث"}.`},
  },
  facility_issue_completed:{
    en:{title:"Facility Issue Resolved",body:d=>`"${d.title||"Issue"}" has been resolved.`},
    ar:{title:"تم حل بلاغ المرفق",body:d=>`"${d.title||"بلاغ"}" تم حله.`},
  },
  /* Household */
  low_stock:{
    en:{title:"Low Stock Alert",body:d=>`${d.item||"Item"}: only ${d.currentQty||0} remaining.`},
    ar:{title:"تنبيه مخزون منخفض",body:d=>`${d.item||"صنف"}: بقي ${d.currentQty||0} فقط.`},
  },
  menu_voting_reminder:{
    en:{title:"Menu Voting Reminder",body:()=>"Weekly menu voting is open. Cast your votes!"},
    ar:{title:"تذكير بالتصويت على القائمة",body:()=>"التصويت على قائمة الطعام الأسبوعية مفتوح. صوّت الآن!"},
  },
  menu_finalized:{
    en:{title:"Weekly Menu Published",body:d=>`The weekly menu${d.weekLabel?" for "+d.weekLabel:""} has been finalized.`},
    ar:{title:"تم نشر قائمة الطعام",body:d=>`تم اعتماد قائمة الطعام الأسبوعية${d.weekLabel?" لـ "+d.weekLabel:""}.`},
  },
  menu_finalized_staff:{
    en:{title:"Weekly Menu — Staff Copy",body:d=>`The weekly menu${d.weekLabel?" for "+d.weekLabel:""} is ready. Please review dietary notes.`},
    ar:{title:"قائمة الطعام — نسخة الموظفين",body:d=>`قائمة الطعام${d.weekLabel?" لـ "+d.weekLabel:""} جاهزة. يرجى مراجعة الملاحظات الغذائية.`},
  },
  /* Calendar */
  meeting_reminder:{
    en:{title:"Meeting Reminder",body:d=>`${d.title||"Meeting"} at ${d.time||"?"}\nLocation: ${d.location||"TBD"}`},
    ar:{title:"تذكير بالاجتماع",body:d=>`${d.title||"اجتماع"} الساعة ${d.time||"?"}\nالموقع: ${d.location||"يحدد لاحقاً"}`},
  },
  /* Smart Home */
  sh_door_opened:{
    en:{title:"Door / Window Opened",body:d=>`${d.entityName||"Sensor"} is now open.${d.area?"\nArea: "+d.area:""}`},
    ar:{title:"تم فتح باب / نافذة",body:d=>`${d.entityName||"مستشعر"} مفتوح الآن.${d.area?"\nالمنطقة: "+d.area:""}`},
  },
  sh_motion_detected:{
    en:{title:"Motion Detected",body:d=>`${d.entityName||"Motion sensor"} detected movement.${d.area?"\nArea: "+d.area:""}`},
    ar:{title:"تم كشف حركة",body:d=>`${d.entityName||"مستشعر حركة"} كشف حركة.${d.area?"\nالمنطقة: "+d.area:""}`},
  },
  sh_lock_changed:{
    en:{title:"Lock State Changed",body:d=>`${d.entityName||"Lock"} is now ${d.newState||"changed"}.${d.area?"\nArea: "+d.area:""}`},
    ar:{title:"تغيير حالة القفل",body:d=>`${d.entityName||"القفل"} الآن ${d.newState==="unlocked"?"مفتوح":"مقفل"}.${d.area?"\nالمنطقة: "+d.area:""}`},
  },
  sh_alarm_triggered:{
    en:{title:"⚠ Alarm Triggered!",body:d=>`${d.entityName||"Alarm"} has been triggered!${d.area?"\nArea: "+d.area:""}`},
    ar:{title:"⚠ تم تفعيل الإنذار!",body:d=>`${d.entityName||"الإنذار"} تم تفعيله!${d.area?"\nالمنطقة: "+d.area:""}`},
  },
  sh_alarm_armed:{
    en:{title:"Alarm State Changed",body:d=>`${d.entityName||"Alarm"} is now ${d.newState||"changed"}.`},
    ar:{title:"تغيير حالة الإنذار",body:d=>`${d.entityName||"الإنذار"} الآن ${d.newState||"متغير"}.`},
  },
  sh_device_offline:{
    en:{title:"Device Offline",body:d=>`${d.entityName||"A device"} is unavailable.${d.area?"\nArea: "+d.area:""}`},
    ar:{title:"جهاز غير متصل",body:d=>`${d.entityName||"جهاز"} غير متاح.${d.area?"\nالمنطقة: "+d.area:""}`},
  },
  /* Grocery */
  grocery_requested:{
    en:{title:"Grocery Request",body:d=>`${d.item||"Item"} x${d.qty||1}${d.unit?" "+d.unit:""}\nBy: ${d.requesterName||"Unknown"}${d.priority==="High"||d.priority==="Urgent"?"\nPriority: "+d.priority:""}`},
    ar:{title:"طلب بقالة",body:d=>`${d.item||"صنف"} x${d.qty||1}${d.unit?" "+d.unit:""}\nمن: ${d.requesterName||"غير معروف"}${d.priority==="High"||d.priority==="Urgent"?"\nالأولوية: "+d.priority:""}`},
  },
  grocery_assigned:{
    en:{title:"Grocery Task Assigned",body:d=>`You have been assigned to buy: ${d.item||"Item"} x${d.qty||1}${d.unit?" "+d.unit:""}`},
    ar:{title:"تم تكليفك بشراء بقالة",body:d=>`تم تكليفك بشراء: ${d.item||"صنف"} x${d.qty||1}${d.unit?" "+d.unit:""}`},
  },
  grocery_fulfilled:{
    en:{title:"Grocery Fulfilled",body:d=>`${d.item||"Item"} has been fulfilled.`},
    ar:{title:"تم تلبية طلب البقالة",body:d=>`${d.item||"صنف"} تم تلبيته.`},
  },
};
/* Helper to get localized notification text */
function getNotifText(eventKey,lang,eventData){
  const entry=NOTIF_TEXT[eventKey];
  if(!entry)return{title:eventKey,body:""};
  const loc=entry[lang]||entry.en||{title:eventKey,body:()=>""};
  return{title:loc.title||eventKey,body:typeof loc.body==="function"?loc.body(eventData||{}):loc.body||""};
}
/* WhatsApp button labels per language */
const WA_BTN_LABELS={
  approve:{en:"Approve",ar:"موافقة"},
  reject:{en:"Reject",ar:"رفض"},
  accept:{en:"Accept",ar:"قبول"},
  decline:{en:"Decline",ar:"رفض"},
};

const NOTIF_EVENTS=[
  /* Registration */
  {key:"registration_email_verify",  label:"Email verification code",        group:"Registration", defaultRoles:["owner"]},
  {key:"registration_password_reset", label:"Password reset code",            group:"Registration", defaultRoles:["owner"]},
  {key:"registration_phone_verify",  label:"Phone verification (WhatsApp)",  group:"Registration", defaultRoles:["owner"]},
  {key:"registration_welcome",       label:"Welcome email after signup",     group:"Registration", defaultRoles:["owner"]},
  {key:"registration_pending",       label:"Pending approval notification",  group:"Registration", defaultRoles:["owner"]},
  /* Billing */
  {key:"billing_invoice",           label:"Payment invoice receipt",        group:"Registration", defaultRoles:["owner"]},
  {key:"billing_payment_failed",    label:"Payment failed notification",    group:"Registration", defaultRoles:["owner"]},
  {key:"billing_cancelled",         label:"Subscription cancelled",         group:"Registration", defaultRoles:["owner"]},
  {key:"billing_expiring",          label:"Subscription expiring soon",     group:"Registration", defaultRoles:["owner"]},
  /* Income */
  {key:"income_received",        label:"Income received",                group:"Income",     defaultRoles:["owner"]},
  {key:"income_overdue",         label:"Unrealised income overdue",      group:"Income",     defaultRoles:["owner","manager"]},
  {key:"recurring_income_due",   label:"Recurring income expected",      group:"Income",     defaultRoles:["owner"]},
  /* Expenses */
  {key:"expense_submitted",      label:"Expense submitted",              group:"Expenses",   defaultRoles:["owner","manager"]},
  {key:"expense_approved",       label:"Expense approved",               group:"Expenses",   defaultRoles:["owner"]},
  {key:"expense_rejected",       label:"Expense rejected",               group:"Expenses",   defaultRoles:["owner","manager"]},
  {key:"large_expense_alert",    label:"Large expense alert",            group:"Expenses",   defaultRoles:["owner"]},
  {key:"recurring_expense_due",  label:"Recurring expense due",          group:"Expenses",   defaultRoles:["owner","manager"]},
  /* Finance — Reports & Summaries */
  {key:"weekly_finance_summary", label:"Weekly finance summary",         group:"Finance",    defaultRoles:["owner"]},
  {key:"monthly_finance_report", label:"Monthly finance report",         group:"Finance",    defaultRoles:["owner"]},
  {key:"cashflow_alert",         label:"Cash flow warning",              group:"Finance",    defaultRoles:["owner","manager"]},
  /* Investments */
  {key:"investment_update",      label:"Portfolio value alert",           group:"Investments", defaultRoles:["owner"]},
  {key:"interest_payment_due",   label:"Interest payment due",            group:"Investments", defaultRoles:["owner"]},
  {key:"liability_maturity_alert",label:"Liability nearing maturity",     group:"Investments", defaultRoles:["owner"]},
  {key:"investment_pnl_alert",   label:"Significant P&L change",          group:"Investments", defaultRoles:["owner"]},
  /* Staff */
  {key:"salary_processed",  label:"Salary payment recorded",    group:"Staff",      defaultRoles:["owner","manager"]},
  {key:"loan_disbursed",    label:"Staff loan disbursed",       group:"Staff",      defaultRoles:["owner","manager"]},
  {key:"leave_requested",   label:"Leave request submitted",    group:"Staff",      defaultRoles:["owner","manager"]},
  {key:"leave_approved",    label:"Leave request approved",     group:"Staff",      defaultRoles:["owner","manager"]},
  /* Operations */
  {key:"transport_request",    label:"Transport request created",    group:"Operations", defaultRoles:["owner","manager","driver","staff_driver"]},
  {key:"transport_completed",  label:"Transport ride completed",     group:"Operations", defaultRoles:["owner","manager"]},
  {key:"transport_cancelled",  label:"Transport ride cancelled",     group:"Operations", defaultRoles:["owner","manager"]},
  {key:"transport_conflict",   label:"Driver scheduling conflict",   group:"Operations", defaultRoles:["owner","manager"]},
  /* Fleet */
  {key:"vehicle_added",          label:"Vehicle added to fleet",       group:"Fleet", defaultRoles:["owner","manager"]},
  {key:"vehicle_updated",        label:"Vehicle details updated",      group:"Fleet", defaultRoles:["owner"]},
  {key:"maintenance_due",        label:"Maintenance / service due",    group:"Fleet", defaultRoles:["owner","manager"]},
  {key:"insurance_expiring",     label:"Vehicle insurance expiring",   group:"Fleet", defaultRoles:["owner","manager"]},
  {key:"istimara_expiring",      label:"Vehicle istimara expiring",    group:"Fleet", defaultRoles:["owner","manager"]},
  /* Suppliers */
  {key:"supplier_added",               label:"Supplier added",                group:"Suppliers", defaultRoles:["owner","manager"]},
  {key:"supplier_updated",             label:"Supplier updated",              group:"Suppliers", defaultRoles:["owner"]},
  {key:"supplier_deactivated",         label:"Supplier deactivated",          group:"Suppliers", defaultRoles:["owner","manager"]},
  {key:"supplier_contract_expiring",   label:"Supplier contract expiring",    group:"Suppliers", defaultRoles:["owner","manager"]},
  /* Facility */
  {key:"facility_issue_created",       label:"Facility issue reported",       group:"Facility", defaultRoles:["owner","manager"]},
  {key:"facility_issue_status_changed",label:"Facility issue status changed", group:"Facility", defaultRoles:["owner","manager"]},
  {key:"facility_issue_completed",     label:"Facility issue completed",      group:"Facility", defaultRoles:["owner","manager"]},
  /* Projects */
  {key:"project_created",             label:"Project created",                group:"Projects", defaultRoles:["owner","manager"]},
  {key:"project_status_changed",      label:"Project status changed",         group:"Projects", defaultRoles:["owner","manager"]},
  {key:"project_deleted",             label:"Project deleted",                group:"Projects", defaultRoles:["owner"]},
  {key:"project_over_budget",         label:"Project over budget",            group:"Projects", defaultRoles:["owner","manager"]},
  {key:"project_member_added",        label:"Project member added",           group:"Projects", defaultRoles:["owner","manager"]},
  {key:"project_member_removed",      label:"Project member removed",         group:"Projects", defaultRoles:["owner","manager"]},
  /* Household */
  {key:"low_stock",            label:"Low stock alert",              group:"Household",  defaultRoles:["owner","manager"]},
  {key:"menu_voting_reminder", label:"Weekly menu voting reminder",  group:"Household",  defaultRoles:["owner","family","manager"]},
  {key:"menu_finalized",       label:"Final menu published (family)", group:"Household",  defaultRoles:["owner","family","manager"]},
  {key:"menu_finalized_staff", label:"Final menu to cook/maid (with dietary note)", group:"Household", defaultRoles:["maid","cook"]},
  /* Calendar */
  {key:"meeting_reminder",   label:"Meeting reminder (1hr)",     group:"Calendar",   defaultRoles:["owner","manager"]},
  /* Smart Home */
  {key:"sh_door_opened",         label:"Door / window opened",          group:"Smart Home", defaultRoles:["owner"]},
  {key:"sh_motion_detected",     label:"Motion detected",               group:"Smart Home", defaultRoles:["owner"]},
  {key:"sh_lock_changed",        label:"Lock state changed",            group:"Smart Home", defaultRoles:["owner"]},
  {key:"sh_alarm_triggered",     label:"Alarm triggered",               group:"Smart Home", defaultRoles:["owner","manager"]},
  {key:"sh_alarm_armed",         label:"Alarm armed / disarmed",        group:"Smart Home", defaultRoles:["owner"]},
  {key:"sh_device_offline",      label:"Device went offline",           group:"Smart Home", defaultRoles:["owner"]},
];
const NOTIF_ROLES=["owner","manager","driver","maid","family"];
const NOTIF_EVENTS_MAP=Object.fromEntries(NOTIF_EVENTS.map(e=>[e.key,e.label]));

/* ── Notification dispatch helper ─────────────────────────
   Call this when an event happens. It checks workspace prefs,
   resolves recipients by role, and fires email + WhatsApp.
   Also creates in-app notifications.
   ──────────────────────────────────────────────────────── */
function fireNotification({eventKey, state, dispatch, platformCtx, title, body, link, templateName, templateVars, buttons, eventData}){
  /* Check if Super Admin has disabled this event globally */
  const platformToggles=platformCtx?.platformNotifToggles||{};
  if(platformToggles[eventKey]===false) return;
  /* Merge channel prefs: platform defaults ← workspace overrides */
  const platformDefaults=(platformCtx?.platformNotifChannelDefaults||{})[eventKey]||{};
  const wsOverrides=(state.notifPrefs||{})[eventKey]||{};
  /* If neither platform nor workspace has configured this event, skip */
  if(!platformDefaults.email&&!platformDefaults.whatsapp&&!platformDefaults.push&&!wsOverrides.email&&!wsOverrides.whatsapp&&!wsOverrides.push) return;
  const prefs={...platformDefaults,...wsOverrides};
  const cfg=platformCtx?.platformNotifConfig||{};
  const emailReady=!!cfg.emailApiKey;
  const waReady=!!(cfg.twilioSid&&cfg.twilioToken&&cfg.twilioFrom);
  const pushReady=!!cfg.pushEnabled;
  const roles=prefs.roles||platformDefaults.roles||NOTIF_EVENTS.find(e=>e.key===eventKey)?.defaultRoles||["owner"];
  const recipients=(state.users||[]).filter(u=>u.active&&roles.includes(u.role));
  const now=new Date();
  const time=now.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  const date=now.toISOString().slice(0,10);
  const eventConfig=(platformCtx?.platformNotifEventConfig||{})[eventKey]||{};
  const eData={...(eventData||{}),appName:state.appName||"Command",date,time};

  recipients.forEach(u=>{
    /* Resolve language per recipient */
    const lang=u.preferredLang||"en";
    const localized=getNotifText(eventKey,lang,eData);
    const lTitle=localized.title||title||eventKey;
    const lBody=localized.body||body||"";
    eData.recipientName=u.name||"";
    eData.title=lTitle;eData.body=lBody;
    const tplSub=(tpl)=>tpl?tpl.replace(/\{\{(\w+)\}\}/g,(_,k)=>eData[k]??""):null;

    /* In-app notification — store both languages for flexibility */
    const enText=getNotifText(eventKey,"en",eData);
    const arText=getNotifText(eventKey,"ar",eData);
    dispatch({type:"ADD_NOTIF",notif:{id:`n_${eventKey}_${Date.now()}_${u.id}`,userId:u.id,type:eventKey,title:enText.title,body:enText.body,titleAr:arText.title,bodyAr:arText.body,link:link||"",read:false,time,date}});

    /* Email — pick language-specific custom template */
    if(prefs.email&&emailReady&&u.email){
      const subjectTpl=lang==="ar"&&eventConfig.emailSubject_ar?eventConfig.emailSubject_ar:eventConfig.emailSubject;
      const htmlTpl=lang==="ar"&&eventConfig.emailHtml_ar?eventConfig.emailHtml_ar:eventConfig.emailHtml;
      const emailSubject=subjectTpl?tplSub(subjectTpl):`[${state.appName||"Command"}] ${lTitle}`;
      const dir=lang==="ar"?'dir="rtl"':"";
      const emailHtml=htmlTpl?tplSub(htmlTpl):`<div ${dir}><p><strong>${lTitle}</strong></p><p>${lBody.replace(/\n/g,"<br/>")}</p><p style="color:#999;font-size:12px">— ${state.appName||"Command"}</p></div>`;
      authFetch("/api/send-email",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({to:u.email,subject:emailSubject,html:emailHtml})}).catch(()=>{});
    }

    /* WhatsApp — pick language-specific template */
    if(prefs.whatsapp&&waReady&&u.phone){
      const payload={to:u.phone};
      const waTplName=lang==="ar"&&eventConfig.waTemplateName_ar?eventConfig.waTemplateName_ar:eventConfig.waTemplateName;
      const waTplVars=lang==="ar"&&eventConfig.waTemplateVars_ar?eventConfig.waTemplateVars_ar:eventConfig.waTemplateVars;
      if(waTplName){
        payload.template=waTplName;
        payload.templateVars=(waTplVars||[]).map(k=>String(eData[k]??""));
        payload.templateLang=lang;
      } else if(templateName){
        payload.template=templateName;
        payload.templateVars=templateVars||[];
        payload.templateLang=lang;
      } else if(buttons&&buttons.length>0){
        const lButtons=buttons.map(b=>{const lk=b.title.toLowerCase();const lb=WA_BTN_LABELS[lk];return{...b,title:lb?lb[lang]||b.title:b.title};});
        payload.message=`*${lTitle}*\n\n${lBody}`;
        payload.buttons=lButtons;
      } else {
        payload.message=`*${lTitle}*\n\n${lBody}`;
      }
      authFetch("/api/send-whatsapp",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)}).catch(()=>{});
    }

    /* Push notification — send via FCM to user's registered devices */
    if(prefs.push&&pushReady){
      authFetch("/api/send-push",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({ws_key:state.wsKey,user_id:u.id,title:lTitle,body:lBody,data:{eventKey,link:link||""}})}).catch(()=>{});
    }
  });
}

function NotificationsTab({state,dispatch,platformCtx}){
  const cfg=platformCtx?.platformNotifConfig||{};
  const emailReady=!!cfg.emailApiKey;
  const waReady=!!(cfg.twilioSid&&cfg.twilioToken&&cfg.twilioFrom);
  const pushReady=!!cfg.pushEnabled;
  const prefs=state.notifPrefs||{};
  const platDefaults=platformCtx?.platformNotifChannelDefaults||{};

  /* Resolve effective state: platform default ← workspace override */
  const effective=(key,channel)=>{
    const ws=prefs[key];
    if(ws&&ws[channel]!==undefined) return ws[channel]; /* explicit workspace override */
    const pd=platDefaults[key];
    if(pd&&pd[channel]!==undefined) return pd[channel]; /* platform default */
    return false;
  };
  const isOverridden=(key,channel)=> prefs[key]&&prefs[key][channel]!==undefined;

  /* Filter events — only show those enabled by Super Admin */
  const platformToggles=platformCtx?.platformNotifToggles||{};
  const VISIBLE_EVENTS=NOTIF_EVENTS.filter(ev=>platformToggles[ev.key]!==false);

  const toggle=(key,field,val)=>dispatch({type:"SET_NOTIF_PREF",key,patch:{[field]:val}});
  const resetChannel=(key,field)=>{
    /* Remove the workspace override so it falls back to platform default */
    const cur={...(prefs[key]||{})};
    delete cur[field];
    dispatch({type:"SET_NOTIF_PREF",key,patch:cur,replace:true});
  };
  const toggleRole=(key,role)=>{
    const cur=(prefs[key]?.roles)||(platDefaults[key]?.roles)||[];
    const next=cur.includes(role)?cur.filter(r=>r!==role):[...cur,role];
    dispatch({type:"SET_NOTIF_PREF",key,patch:{roles:next}});
  };

  // Master channel controls — set all events at once
  const setAllChannel=(channel,val)=>{
    VISIBLE_EVENTS.forEach(ev=>dispatch({type:"SET_NOTIF_PREF",key:ev.key,patch:{[channel]:val}}));
  };
  const resetAllChannel=(channel)=>{
    VISIBLE_EVENTS.forEach(ev=>{
      const cur={...(prefs[ev.key]||{})};
      delete cur[channel];
      dispatch({type:"SET_NOTIF_PREF",key:ev.key,patch:cur,replace:true});
    });
  };
  const allEmailOn=VISIBLE_EVENTS.every(ev=>effective(ev.key,"email"));
  const allWaOn=VISIBLE_EVENTS.every(ev=>effective(ev.key,"whatsapp"));
  const allPushOn=VISIBLE_EVENTS.every(ev=>effective(ev.key,"push"));

  const groups=[...new Set(VISIBLE_EVENTS.map(e=>e.group))];
  return(
    <div>
      <div className="ph" style={{paddingBottom:0}}><div><div className="ptitle">Notifications</div><div className="psub">Toggle notification channels on or off for this workspace. Defaults are set by the platform admin.</div></div></div>
      {(!emailReady&&!waReady&&!pushReady)&&(
        <div className="notice" style={{margin:"18px 0",borderColor:"var(--gold-bd)",background:"var(--gold-bg)"}}>
          <div className="notice-l">Setup required</div>
          Email, WhatsApp, and push are not yet configured. A super admin must add provider credentials in the Super Admin → Notifications settings before messages can be sent.
        </div>
      )}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",margin:"14px 0",fontSize:12}}>
        <span style={{display:"flex",alignItems:"center",gap:5,padding:"4px 12px",borderRadius:20,background:emailReady?"#E8F5E9":"var(--cg50)",border:"1.5px solid "+(emailReady?"#4CAF50":"var(--cg200)"),color:emailReady?"#2E7D32":"var(--cg400)",fontWeight:600}}>
          ✉ Email {emailReady?"ready":"not configured"}
        </span>
        <span style={{display:"flex",alignItems:"center",gap:5,padding:"4px 12px",borderRadius:20,background:waReady?"#E8F5E9":"var(--cg50)",border:"1.5px solid "+(waReady?"#4CAF50":"var(--cg200)"),color:waReady?"#2E7D32":"var(--cg400)",fontWeight:600}}>
          💬 WhatsApp {waReady?"ready":"not configured"}
        </span>
        <span style={{display:"flex",alignItems:"center",gap:5,padding:"4px 12px",borderRadius:20,background:pushReady?"#E8F5E9":"var(--cg50)",border:"1.5px solid "+(pushReady?"#4CAF50":"var(--cg200)"),color:pushReady?"#2E7D32":"var(--cg400)",fontWeight:600}}>
          🔔 Push {pushReady?"ready":"not configured"}
        </span>
      </div>
      <div className="notice" style={{marginBottom:18}}>
        <div className="notice-l">Phone numbers</div>
        WhatsApp messages are sent to the phone number saved on each user's profile. Add or update phone numbers in Settings → Users.
      </div>
      {/* Master channel switches */}
      <div className="card" style={{padding:"14px 16px",marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:12}}>Workspace channel overrides</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{fontSize:12,color:"var(--g600)",marginInlineEnd:4}}>✉ Email</div>
          <button onClick={()=>setAllChannel("email",true)} style={{fontSize:12,padding:"5px 14px",borderRadius:20,border:`1.5px solid ${allEmailOn?"#4CAF50":"var(--g200)"}`,background:allEmailOn?"#E8F5E9":"transparent",color:allEmailOn?"#2E7D32":"var(--g500)",cursor:"pointer",fontWeight:600,opacity:emailReady?1:.45}}>All on</button>
          <button onClick={()=>setAllChannel("email",false)} style={{fontSize:12,padding:"5px 14px",borderRadius:20,border:"1.5px solid var(--g200)",background:"transparent",color:"var(--g500)",cursor:"pointer",fontWeight:600,opacity:emailReady?1:.45}}>All off</button>
          <button onClick={()=>resetAllChannel("email")} style={{fontSize:11,padding:"4px 10px",borderRadius:20,border:"1.5px dashed var(--g200)",background:"transparent",color:"var(--g400)",cursor:"pointer"}}>Reset</button>
          <div style={{width:1,height:20,background:"var(--g200)",margin:"0 6px"}}/>
          <div style={{fontSize:12,color:"var(--g600)",marginInlineEnd:4}}>💬 WhatsApp</div>
          <button onClick={()=>setAllChannel("whatsapp",true)} style={{fontSize:12,padding:"5px 14px",borderRadius:20,border:`1.5px solid ${allWaOn?"#25D366":"var(--g200)"}`,background:allWaOn?"#E8F5E9":"transparent",color:allWaOn?"#1a7a42":"var(--g500)",cursor:"pointer",fontWeight:600,opacity:waReady?1:.45}}>All on</button>
          <button onClick={()=>setAllChannel("whatsapp",false)} style={{fontSize:12,padding:"5px 14px",borderRadius:20,border:"1.5px solid var(--g200)",background:"transparent",color:"var(--g500)",cursor:"pointer",fontWeight:600,opacity:waReady?1:.45}}>All off</button>
          <button onClick={()=>resetAllChannel("whatsapp")} style={{fontSize:11,padding:"4px 10px",borderRadius:20,border:"1.5px dashed var(--g200)",background:"transparent",color:"var(--g400)",cursor:"pointer"}}>Reset</button>
          <div style={{width:1,height:20,background:"var(--g200)",margin:"0 6px"}}/>
          <div style={{fontSize:12,color:"var(--g600)",marginInlineEnd:4}}>🔔 Push</div>
          <button onClick={()=>setAllChannel("push",true)} style={{fontSize:12,padding:"5px 14px",borderRadius:20,border:`1.5px solid ${allPushOn?"#FF6D00":"var(--g200)"}`,background:allPushOn?"#FFF3E0":"transparent",color:allPushOn?"#E65100":"var(--g500)",cursor:"pointer",fontWeight:600,opacity:pushReady?1:.45}}>All on</button>
          <button onClick={()=>setAllChannel("push",false)} style={{fontSize:12,padding:"5px 14px",borderRadius:20,border:"1.5px solid var(--g200)",background:"transparent",color:"var(--g500)",cursor:"pointer",fontWeight:600,opacity:pushReady?1:.45}}>All off</button>
          <button onClick={()=>resetAllChannel("push")} style={{fontSize:11,padding:"4px 10px",borderRadius:20,border:"1.5px dashed var(--g200)",background:"transparent",color:"var(--g400)",cursor:"pointer"}}>Reset</button>
        </div>
        <div style={{fontSize:11,color:"var(--g400)",marginTop:10,lineHeight:1.5}}>Override platform defaults for this workspace. "Reset" restores the platform admin's defaults. Dashed borders indicate inherited defaults; solid borders indicate workspace overrides. Notification content and templates are managed by the platform admin.</div>
      </div>
      {groups.map(group=>{
        const events=VISIBLE_EVENTS.filter(e=>e.group===group);
        const platformDefs=platformCtx?.platformInvestmentAlertDefaults||{interestPaymentDueDays:7,liabilityMaturityDays:30};
        const wsInvestAlerts=state.investmentAlertSettings||{};
        const interestDays=wsInvestAlerts.interestPaymentDueDays??platformDefs.interestPaymentDueDays??7;
        const maturityDays=wsInvestAlerts.liabilityMaturityDays??platformDefs.liabilityMaturityDays??30;
        return(
          <div key={group} className="card" style={{marginBottom:12,padding:0,overflow:"hidden"}}>
            <div style={{padding:"10px 16px",background:"var(--g50)",borderBottom:"1px solid var(--g100)",fontSize:11,fontWeight:700,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".07em"}}>{group}</div>
            {group==="Income"&&(()=>{
              const incPlatDefs=platformCtx?.platformIncomeAlertDefaults||{incomeOverdueDays:3};
              const wsIncAlerts=state.incomeAlertSettings||{};
              const overdueDays=wsIncAlerts.incomeOverdueDays??incPlatDefs.incomeOverdueDays??3;
              return(
              <div style={{padding:"12px 16px",borderBottom:"1px solid var(--g100)",background:"var(--g25,#FAFAFA)"}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--ink)",marginBottom:8}}>Alert timing</div>
                <div style={{display:"flex",gap:20,flexWrap:"wrap",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:12,color:"var(--g600)"}}>Unrealised income overdue</span>
                    <input className="fi" type="number" min="0" max="90" value={overdueDays} style={{width:56,padding:"4px 8px",fontSize:12,textAlign:"center"}}
                      onChange={e=>dispatch({type:"SET_INCOME_ALERT_SETTINGS",patch:{incomeOverdueDays:parseInt(e.target.value)||3}})}/>
                    <span style={{fontSize:11,color:"var(--g400)"}}>days after due date</span>
                  </div>
                </div>
                <div style={{fontSize:11,color:"var(--g400)",marginTop:6}}>Platform default: {incPlatDefs.incomeOverdueDays} days. Override here for this workspace.</div>
              </div>);
            })()}
            {group==="Expenses"&&(()=>{
              const expPlatDefs=platformCtx?.platformExpenseAlertDefaults||{budgetWarningPercent:80};
              const wsExpAlerts=state.expenseAlertSettings||{};
              const warningPct=wsExpAlerts.budgetWarningPercent??expPlatDefs.budgetWarningPercent??80;
              return(
              <div style={{padding:"12px 16px",borderBottom:"1px solid var(--g100)",background:"var(--g25,#FAFAFA)"}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--ink)",marginBottom:8}}>Alert threshold</div>
                <div style={{display:"flex",gap:20,flexWrap:"wrap",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:12,color:"var(--g600)"}}>Budget warning at</span>
                    <input className="fi" type="number" min="50" max="100" value={warningPct} style={{width:56,padding:"4px 8px",fontSize:12,textAlign:"center"}}
                      onChange={e=>dispatch({type:"SET_EXPENSE_ALERT_SETTINGS",patch:{budgetWarningPercent:parseInt(e.target.value)||80}})}/>
                    <span style={{fontSize:11,color:"var(--g400)"}}>% of budget</span>
                  </div>
                </div>
                <div style={{fontSize:11,color:"var(--g400)",marginTop:6}}>Platform default: {expPlatDefs.budgetWarningPercent}%. Override here for this workspace.</div>
              </div>);
            })()}
            {group==="Investments"&&(
              <div style={{padding:"12px 16px",borderBottom:"1px solid var(--g100)",background:"var(--g25,#FAFAFA)"}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--ink)",marginBottom:8}}>Alert timing</div>
                <div style={{display:"flex",gap:20,flexWrap:"wrap",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:12,color:"var(--g600)"}}>Interest payment due</span>
                    <input className="fi" type="number" min="1" max="90" value={interestDays} style={{width:56,padding:"4px 8px",fontSize:12,textAlign:"center"}}
                      onChange={e=>dispatch({type:"SET_INVEST_ALERT_SETTINGS",patch:{interestPaymentDueDays:parseInt(e.target.value)||7}})}/>
                    <span style={{fontSize:11,color:"var(--g400)"}}>days before</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:12,color:"var(--g600)"}}>Liability maturity</span>
                    <input className="fi" type="number" min="1" max="365" value={maturityDays} style={{width:56,padding:"4px 8px",fontSize:12,textAlign:"center"}}
                      onChange={e=>dispatch({type:"SET_INVEST_ALERT_SETTINGS",patch:{liabilityMaturityDays:parseInt(e.target.value)||30}})}/>
                    <span style={{fontSize:11,color:"var(--g400)"}}>days before</span>
                  </div>
                </div>
                <div style={{fontSize:11,color:"var(--g400)",marginTop:6}}>Platform default: {platformDefs.interestPaymentDueDays} / {platformDefs.liabilityMaturityDays} days. Override here for this workspace.</div>
              </div>
            )}
            {events.map((ev,i)=>{
              const eOn=effective(ev.key,"email");
              const wOn=effective(ev.key,"whatsapp");
              const pOn=effective(ev.key,"push");
              const eOvr=isOverridden(ev.key,"email");
              const wOvr=isOverridden(ev.key,"whatsapp");
              const pOvr=isOverridden(ev.key,"push");
              const channelBtn=(label,icon,on,ovr,channel,readyOpacity)=>(
                <span style={{display:"inline-flex",alignItems:"center",gap:3}}>
                  <button onClick={()=>toggle(ev.key,channel,!on)}
                    style={{fontSize:11,padding:"3px 10px",borderRadius:20,border:`1.5px ${ovr?"solid":"dashed"} ${on?(channel==="push"?"#FF6D00":channel==="whatsapp"?"#25D366":"#4CAF50"):"var(--g200)"}`,background:on?(channel==="push"?"#FFF3E0":channel==="whatsapp"?"#E8F5E9":"#E8F5E9"):"transparent",color:on?(channel==="push"?"#E65100":channel==="whatsapp"?"#1a7a42":"#2E7D32"):"var(--g400)",cursor:"pointer",fontWeight:600,opacity:readyOpacity}}>
                    {icon} {on?"On":"Off"}
                  </button>
                  {ovr&&<button onClick={()=>resetChannel(ev.key,channel)} title="Reset to platform default" style={{fontSize:9,padding:"1px 5px",borderRadius:10,border:"1px dashed var(--g300)",background:"transparent",color:"var(--g400)",cursor:"pointer",lineHeight:1}}>x</button>}
                </span>
              );
              const wsRoles=prefs[ev.key]?.roles;
              const platRoles=(platDefaults[ev.key]?.roles)||ev.defaultRoles;
              const roles=wsRoles||platRoles;
              return(
                <div key={ev.key} style={{padding:"12px 16px",borderBottom:i<events.length-1?"1px solid var(--g100)":"none"}}>
                  <div className="notif-row" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                    <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{ev.label}</div>
                    <div className="notif-controls" style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                      {channelBtn("Email","✉",eOn,eOvr,"email",emailReady?1:.45)}
                      {channelBtn("WhatsApp","💬",wOn,wOvr,"whatsapp",waReady?1:.45)}
                      {channelBtn("Push","🔔",pOn,pOvr,"push",pushReady?1:.45)}
                      {/* Role pills — workspace can override platform default roles */}
                      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                        {NOTIF_ROLES.map(role=>{
                          const active=roles.includes(role);
                          return(
                            <span key={role} onClick={()=>toggleRole(ev.key,role)}
                              style={{fontSize:10,padding:"2px 8px",borderRadius:20,border:`1.5px ${wsRoles?"solid":"dashed"} ${active?"var(--gold)":"var(--g200)"}`,background:active?"var(--gold-bg)":"transparent",color:active?"var(--gold-dk)":"var(--g400)",cursor:"pointer",fontFamily:"var(--fm)",textTransform:"capitalize",transition:"all .1s"}}>
                              {role}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      <div style={{fontSize:12,color:"var(--g400)",marginTop:8,lineHeight:1.6}}>
        Notification triggers will be wired to workspace events in an upcoming update. Preferences saved here are ready and will be applied automatically.
      </div>
    </div>
  );
}



export { EVENT_PARAMS, EVENT_VARIABLES, EVENT_SAMPLE_DATA, COMMON_VARIABLES, defaultEmailHtml, applyTemplate, NOTIF_EVENTS, NOTIF_EVENTS_MAP, NOTIF_ROLES, WA_BTN_LABELS, getNotifText, fireNotification, NotificationsTab };
